import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronRight, FiHome } from 'react-icons/fi';

interface BreadcrumbProps {
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ className = '' }) => {
  const location = useLocation();
  const paths = location.pathname.split('/').filter((path) => path !== '');

  // Function to format the path text
  const formatPathName = (path: string): string => {
    return path
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <nav
      className={`hidden sm:flex ${className} px-4 sm:px-0`}
      aria-label="Breadcrumb"
    >
      <ol className="flex flex-wrap items-center gap-y-2">
        {/* Home Link */}
        <li className="flex items-center">
          <Link
            to="/"
            className="flex items-center text-gray-500 hover:text-primary-950 transition-colors duration-200"
          >
            <FiHome className="w-4 h-4" />
            <span className="hidden sm:inline-block ml-1">Home</span>
          </Link>
        </li>

        {/* Separator after Home */}
        <li className="flex items-center mx-2">
          <FiChevronRight className="w-3.5 h-3.5 text-gray-400" />
        </li>

        {/* Dynamic Path Links */}
        {paths.map((path, index) => {
          const isLast = index === paths.length - 1;
          const linkPath = `/${paths.slice(0, index + 1).join('/')}`;

          return (
            <React.Fragment key={path}>
              <li className="flex items-center">
                {isLast ? (
                  <span className="text-primary-950 font-medium">
                    {formatPathName(path)}
                  </span>
                ) : (
                  <Link
                    to={linkPath}
                    className="text-sm text-gray-500 hover:text-primary-950 transition-colors duration-200"
                  >
                    {formatPathName(path)}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li className="flex items-center mx-2">
                  <FiChevronRight className="w-3.5 h-3.5 text-gray-400" />
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>

      {/* Mobile Truncated View for long paths */}
      {paths.length > 2 && (
        <ol className="sm:hidden flex items-center mt-1 text-xs text-gray-500">
          <li>
            <span>
              .../{paths[paths.length - 1]}
            </span>
          </li>
        </ol>
      )}
    </nav>
  );
};

// Optional: Add a loading skeleton for the breadcrumb
export const BreadcrumbSkeleton: React.FC = () => (
  <div className="animate-pulse flex items-center gap-2">
    <div className="h-4 w-4 bg-gray-200 rounded" />
    <div className="h-4 w-16 bg-gray-200 rounded" />
    <div className="h-4 w-4 bg-gray-200 rounded" />
    <div className="h-4 w-24 bg-gray-200 rounded" />
  </div>
);

export default Breadcrumb;